"use client";

import { useId, type ComponentType, type ReactNode } from "react";

/* Role/tier badges — one glyph per role group, finish = seniority.
   No tile or backing: the glyph IS the badge.

   Basic      — flat color (per-group: mod orange, staff blue, dev
                green; default warm gray)
   Golden     — warm gold gradient, three static sparkles
   Shiny      — brighter gold, sparkles twinkle + a sheen sweeps
   Impeccable — animated teal→peach gradient + twinkling sparkles

   tierSet() builds all four off any glyph. Stroke-mode is for
   tabler-style line icons (fill none, stroke 2) — gradients paint the
   stroke and the sheen is cut by a luminance mask (clipPath can't see
   stroke geometry). Fill-mode glyphs (solid shapes) get a plain clip. */

export interface TierGlyph {
  /* true = tabler-style stroke icon (fill none / stroke currentColor);
     false = solid fill shape */
  stroke?: boolean;
  paths: ReactNode;
  /* Basic-tier flat color — defaults to warm gray */
  flat?: string;
}

export type TierComponent = ComponentType<{ className?: string }>;

/* ── sparkles ──────────────────────────────────────────────────── */

/* four-point sparkle — centered on 0,0 so the twinkle scale pivots in
   place; scale it via transform on the wrapping <g> */
const SPARKLE =
  "M0 -4.4 L1.15 -1.15 4.4 0 1.15 1.15 0 4.4 -1.15 1.15 -4.4 0 -1.15 -1.15 Z";

const Twinkle = ({
  x,
  y,
  s = 1,
  fill = "#fff6cf",
}: {
  x: number;
  y: number;
  s?: number;
  fill?: string;
}) => (
  <path transform={`translate(${x} ${y}) scale(${s})`} d={SPARKLE} fill={fill} />
);

/* the same sparkle, twinkling — SMIL scale 0→1→0 on a loop, `begin`
   staggers them so they pop one at a time */
const TwinkleLive = ({
  x,
  y,
  s = 1,
  dur = "1.6s",
  begin = "0s",
  fill = "#ffffff",
}: {
  x: number;
  y: number;
  s?: number;
  dur?: string;
  begin?: string;
  fill?: string;
}) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <g>
      <animateTransform
        attributeName="transform"
        type="scale"
        values="0;1;0"
        keyTimes="0;0.5;1"
        dur={dur}
        begin={begin}
        repeatCount="indefinite"
      />
      <path d={SPARKLE} fill={fill} />
    </g>
  </g>
);

/* the three sparkle anchors — top-right, mid-left, bottom-right */
const SPARKS: [number, number, number][] = [
  [19, 4.6, 0.85],
  [4.9, 7.4, 0.55],
  [17.4, 19.2, 0.5],
];

/* ── shared finishes ───────────────────────────────────────────── */

/* gold — brighter on Shiny since the sheen needs the contrast */
const goldStops = (bright: boolean) => (
  <>
    <stop offset="0" stopColor={bright ? "#fbe98a" : "#f7d968"} />
    <stop offset="0.55" stopColor={bright ? "#e8b547" : "#e2a83d"} />
    <stop offset="1" stopColor={bright ? "#c08616" : "#b07b14"} />
  </>
);

/* the glyph drawn as ink — filled or stroked depending on its style.
   `paint` is a color or url(#grad) */
const ink = (g: TierGlyph, paint: string) =>
  g.stroke ? (
    <g
      fill="none"
      stroke={paint}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {g.paths}
    </g>
  ) : (
    <g fill={paint}>{g.paths}</g>
  );

/* sheen containment — solid glyphs clip to their fill shape; stroke
   glyphs mask to their white-paint luminance */
const shearBounds = (uid: string, g: TierGlyph) =>
  g.stroke ? (
    <mask
      id={`tm${uid}`}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      {ink(g, "#fff")}
    </mask>
  ) : (
    <clipPath id={`tm${uid}`}>{g.paths}</clipPath>
  );

const shearAttr = (uid: string, g: TierGlyph) =>
  g.stroke
    ? { mask: `url(#tm${uid})` }
    : { clipPath: `url(#tm${uid})` };

/* ── the factory ───────────────────────────────────────────────── */

export function tierSet(
  name: string,
  glyph: TierGlyph,
): {
  Basic: TierComponent;
  Golden: TierComponent;
  Shiny: TierComponent;
  Impeccable: TierComponent;
} {
  const Basic: TierComponent = ({ className }) => (
    <svg
      viewBox="0 0 24 24"
      className={className}
      role="img"
      aria-label={`${name} badge`}
    >
      {ink(glyph, glyph.flat ?? "#8f8a7e")}
    </svg>
  );
  Basic.displayName = `${name}Basic`;

  const Golden: TierComponent = ({ className }) => {
    const uid = useId();
    return (
      <svg
        viewBox="0 0 24 24"
        className={className}
        role="img"
        aria-label={`${name} badge`}
      >
        <defs>
          <linearGradient
            id={`tg${uid}`}
            x1="4"
            y1="2"
            x2="20"
            y2="22"
            gradientUnits="userSpaceOnUse"
          >
            {goldStops(false)}
          </linearGradient>
        </defs>
        {ink(glyph, `url(#tg${uid})`)}
        {SPARKS.map(([x, y, s]) => (
          <Twinkle key={`${x}${y}`} x={x} y={y} s={s} />
        ))}
      </svg>
    );
  };
  Golden.displayName = `${name}Golden`;

  const Shiny: TierComponent = ({ className }) => {
    const uid = useId();
    return (
      <svg
        viewBox="0 0 24 24"
        className={className}
        role="img"
        aria-label={`${name} badge`}
      >
        <defs>
          <linearGradient
            id={`tg${uid}`}
            x1="4"
            y1="2"
            x2="20"
            y2="22"
            gradientUnits="userSpaceOnUse"
          >
            {goldStops(true)}
          </linearGradient>
          {/* the sheen band — bright core, transparent edges */}
          <linearGradient id={`ts${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.5" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          {shearBounds(uid, glyph)}
        </defs>
        {ink(glyph, `url(#tg${uid})`)}
        {/* sheen sweep — a rotated band slides across the glyph, held
            inside its shape by the clip/mask */}
        <g {...shearAttr(uid, glyph)}>
          <g transform="rotate(-18 12 12)">
            <rect
              x="-14"
              y="-8"
              width="7"
              height="40"
              fill={`url(#ts${uid})`}
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="-40 0; 90 0"
                dur="2.6s"
                repeatCount="indefinite"
              />
            </rect>
          </g>
        </g>
        {SPARKS.map(([x, y, s], i) => (
          <TwinkleLive
            key={`${x}${y}`}
            x={x}
            y={y}
            s={s}
            dur={`${1.5 + i * 0.2}s`}
            begin={`${i * 0.5}s`}
          />
        ))}
      </svg>
    );
  };
  Shiny.displayName = `${name}Shiny`;

  const Impeccable: TierComponent = ({ className }) => {
    const uid = useId();
    return (
      <svg
        viewBox="0 0 24 24"
        className={className}
        role="img"
        aria-label={`${name} badge`}
      >
        <defs>
          <linearGradient
            id={`ti${uid}`}
            x1="2"
            y1="2"
            x2="22"
            y2="22"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#58c4b4" />
            <stop offset="0.5" stopColor="#9aa8e8" />
            <stop offset="1" stopColor="#f2a97e" />
            {/* the gradient itself rotates around the glyph center —
                that's the "moving" finish */}
            <animateTransform
              attributeName="gradientTransform"
              type="rotate"
              from="0 12 12"
              to="360 12 12"
              dur="5s"
              repeatCount="indefinite"
            />
          </linearGradient>
        </defs>
        {ink(glyph, `url(#ti${uid})`)}
        {SPARKS.map(([x, y, s], i) => (
          <TwinkleLive
            key={`${x}${y}`}
            x={x}
            y={y}
            s={s}
            dur={`${1.4 + i * 0.2}s`}
            begin={`${i * 0.6}s`}
          />
        ))}
      </svg>
    );
  };
  Impeccable.displayName = `${name}Impeccable`;

  return { Basic, Golden, Shiny, Impeccable };
}

/* ── glyph sets (real tabler paths, stroke-mode) ───────────────── */

/* IconSwords — the Mod ladder */
const Swords = tierSet("Swords", {
  stroke: true,
  flat: "#ee8a34" /* warm orange — palette's sleepy tone */,
  paths: (
    <>
      <path d="M21 3v5l-11 9l-4 4l-3 -3l4 -4l9 -11z" />
      <path d="M5 13l6 6" />
      <path d="M14.32 17.32l3.68 3.68l3 -3l-3.365 -3.365" />
      <path d="M10 5.5l-2 -2.5h-5v5l3 2.5" />
    </>
  ),
});

/* IconUserShield — the Staff ladder */
const Shield = tierSet("UserShield", {
  stroke: true,
  flat: "#7d8fe0" /* accent blue */,
  paths: (
    <>
      <path d="M6 21v-2a4 4 0 0 1 4 -4h2" />
      <path d="M22 16c0 4 -2.5 6 -3.5 6s-3.5 -2 -3.5 -6c1 0 2.5 -.5 3.5 -1.5c1 1 2.5 1.5 3.5 1.5z" />
      <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
    </>
  ),
});

/* IconCode — the Dev ladder */
const Code = tierSet("Code", {
  stroke: true,
  flat: "#2fbf71" /* green — palette's online tone */,
  paths: (
    <>
      <path d="M6 19a2 2 0 0 1 -2 -2v-4l-1 -1l1 -1v-4a2 2 0 0 1 2 -2" />
      <path d="M12 11.875l3 -1.687" />
      <path d="M12 11.875v3.375" />
      <path d="M12 11.875l-3 -1.687" />
      <path d="M12 11.875l3 1.688" />
      <path d="M12 8.5v3.375" />
      <path d="M12 11.875l-3 1.688" />
      <path d="M18 19a2 2 0 0 0 2 -2v-4l1 -1l-1 -1v-4a2 2 0 0 0 -2 -2" />
    </>
  ),
});

/* IconHexagonLetterG — Goaded, founder */
const HexG = tierSet("Goaded", {
  stroke: true,
  paths: (
    <>
      <path d="M19.875 6.27a2.225 2.225 0 0 1 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z" />
      <path d="M14 8h-2a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2v-4h-1" />
    </>
  ),
});

/* IconHexagonLetterR — RoadSign, founder */
const HexR = tierSet("RoadSign", {
  stroke: true,
  paths: (
    <>
      <path d="M19.875 6.27a2.225 2.225 0 0 1 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z" />
      <path d="M10 12h2a2 2 0 1 0 0 -4h-2v8m4 0l-3 -4" />
    </>
  ),
});

/* IconBrush — the Designer ladder */
const Brush = tierSet("Brush", {
  stroke: true,
  flat: "#9b7bff" /* purple — palette's working tone */,
  paths: (
    <>
      <path d="M3 21v-4a4 4 0 1 1 4 4h-4" />
      <path d="M21 3a16 16 0 0 0 -12.8 10.2" />
      <path d="M21 3a16 16 0 0 1 -10.2 12.8" />
      <path d="M10.6 9a9 9 0 0 1 4.4 4.4" />
    </>
  ),
});

/* IconAdCircle — the Marketer ladder */
const AdCircle = tierSet("AdCircle", {
  stroke: true,
  flat: "#f2b544" /* amber — palette's idle tone */,
  paths: (
    <>
      <path d="M12 12m-10 0a10 10 0 1 0 20 0a10 10 0 1 0 -20 0" />
      <path d="M7 15v-4.5a1.5 1.5 0 0 1 3 0v4.5" />
      <path d="M7 13h3" />
      <path d="M14 9v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
    </>
  ),
});

/* IconCircleLetterT — Tyler, owner */
const CircleT = tierSet("Tyler", {
  stroke: true,
  paths: (
    <>
      <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
      <path d="M10 8h4" />
      <path d="M12 8v8" />
    </>
  ),
});

/* ── milestone glyph sets ──────────────────────────────────────── */

/* IconCake — "First Day" */
export const CakeTiers = tierSet("Cake", {
  stroke: true,
  paths: (
    <>
      <path d="M3 20h18v-8a3 3 0 0 0 -3 -3h-12a3 3 0 0 0 -3 3v8z" />
      <path d="M3 14.803c.312 .135 .654 .204 1 .197a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1a2.4 2.4 0 0 0 2 -1a2.4 2.4 0 0 1 2 -1a2.4 2.4 0 0 1 2 1a2.4 2.4 0 0 0 2 1c.35 .007 .692 -.062 1 -.197" />
      <path d="M12 4l1.465 1.638a2 2 0 1 1 -3.015 .099l1.55 -1.737z" />
    </>
  ),
});

/* IconConfetti — "First 100 Members" */
export const ConfettiTiers = tierSet("Confetti", {
  stroke: true,
  paths: (
    <>
      <path d="M4 5h2" />
      <path d="M5 4v2" />
      <path d="M11.5 4l-.5 2" />
      <path d="M18 5h2" />
      <path d="M19 4v2" />
      <path d="M15 9l-1 1" />
      <path d="M18 13l2 -.5" />
      <path d="M18 19h2" />
      <path d="M19 18v2" />
      <path d="M14 16.518l-6.518 -6.518l-4.39 9.58a1 1 0 0 0 1.329 1.329l9.579 -4.39z" />
    </>
  ),
});

/* IconBug — Bug Hunter / Super Bug Hunter */
export const BugTiers = tierSet("Bug", {
  stroke: true,
  flat: "#2fbf71",
  paths: (
    <>
      <path d="M9 9v-1a3 3 0 0 1 6 0v1" />
      <path d="M8 9h8a6 6 0 0 1 1 3v3a5 5 0 0 1 -10 0v-3a6 6 0 0 1 1 -3" />
      <path d="M3 13l4 0" />
      <path d="M17 13l4 0" />
      <path d="M12 20l0 -6" />
      <path d="M4 19l3.35 -2" />
      <path d="M20 19l-3.35 -2" />
      <path d="M4 7l3.75 2.4" />
      <path d="M20 7l-3.75 2.4" />
    </>
  ),
});

/* ── misc + registry ───────────────────────────────────────────── */

/* Icey — the favicon itself, gray flat */
export const IceyBadge: TierComponent = ({ className }) => (
  // eslint-disable-next-line @next/next/no-img-element -- logo asset
  <img
    src="/favicon.webp"
    alt="Icey badge"
    className={`${className ?? ""} opacity-80 grayscale`}
  />
);
IceyBadge.displayName = "IceyBadge";

/* Super Icey — the favicon masked to the moving teal→peach gradient +
   twinkles. the favicon's a raster, so the gradient goes behind a
   luminance mask instead of painting strokes */
export const IceySuperBadge: TierComponent = ({ className }) => (
  <span className={`relative inline-block ${className ?? ""}`}>
    <span
      aria-hidden
      className="icey-super block h-full w-full"
      style={{
        WebkitMaskImage: "url(/favicon.webp)",
        maskImage: "url(/favicon.webp)",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
      }}
    />
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
    >
      <TwinkleLive x={20} y={4.2} s={0.8} dur="1.6s" />
      <TwinkleLive x={4} y={8} s={0.55} dur="1.9s" begin="0.7s" />
    </svg>
  </span>
);
IceySuperBadge.displayName = "IceySuperBadge";

/* role → badge component — the whole ladder keyed for rendering */
export const ROLE_BADGES = [
  /* Mods */
  { key: "jr-mod", name: "Jr.Mod", group: "Mods", Icon: Swords.Basic },
  { key: "mod", name: "Mod", group: "Mods", Icon: Swords.Golden },
  { key: "sr-mod", name: "Sr.Mod", group: "Mods", Icon: Swords.Shiny },
  { key: "super-mod", name: "Super mod", group: "Mods", Icon: Swords.Impeccable },
  /* Staff */
  { key: "staff", name: "Staff", group: "Staff", Icon: Shield.Basic },
  { key: "super-staff", name: "Super staff", group: "Staff", Icon: Shield.Shiny },
  { key: "staff-manager", name: "Staff Manager", group: "Staff", Icon: Shield.Impeccable },
  /* Devs */
  { key: "jr-dev", name: "Jr. Dev", group: "Devs", Icon: Code.Basic },
  { key: "dev", name: "Dev", group: "Devs", Icon: Code.Golden },
  { key: "sr-dev", name: "Sr. Dev", group: "Devs", Icon: Code.Shiny },
  { key: "super-dev", name: "Super Dev", group: "Devs", Icon: Code.Impeccable },
  /* Designers */
  { key: "jr-designer", name: "Jr. Designer", group: "Designers", Icon: Brush.Basic },
  { key: "designer", name: "Designer", group: "Designers", Icon: Brush.Golden },
  { key: "sr-designer", name: "Sr. Designer", group: "Designers", Icon: Brush.Shiny },
  { key: "super-designer", name: "Super Designer", group: "Designers", Icon: Brush.Impeccable },
  /* Marketers */
  { key: "jr-marketer", name: "Jr. Marketer", group: "Marketers", Icon: AdCircle.Basic },
  { key: "marketer", name: "Marketer", group: "Marketers", Icon: AdCircle.Golden },
  { key: "sr-marketer", name: "Sr. Marketer", group: "Marketers", Icon: AdCircle.Shiny },
  { key: "super-marketer", name: "Super Marketer", group: "Marketers", Icon: AdCircle.Impeccable },
  /* Misc */
  { key: "icey", name: "Icey", group: "Misc", Icon: IceyBadge },
  { key: "super-icey", name: "Super Icey", group: "Misc", Icon: IceySuperBadge },
  /* Founders */
  { key: "goaded", name: "Goaded", group: "Founders", Icon: HexG.Impeccable },
  { key: "roadsign", name: "RoadSign", group: "Founders", Icon: HexR.Impeccable },
  /* Owner */
  { key: "tyler", name: "Tyler", group: "Owner", Icon: CircleT.Shiny },
] as const;

export type RoleKey = (typeof ROLE_BADGES)[number]["key"];

export const ROLE_BADGE = Object.fromEntries(
  ROLE_BADGES.map((r) => [r.key, r.Icon]),
) as Record<RoleKey, TierComponent>;

/* the app's badge map — role keys + milestone keys. Lives HERE (not
   data.ts) because data.ts is shared with server components and client
   modules' runtime values don't exist server-side — this file is
   "use client" so the components only resolve on the client */
export type BadgeKey =
  | RoleKey
  | "owner"
  | "joinday"
  | "first100"
  | "bug"
  | "bugbounty"
  | "goldbug"
  | "superbugbounty";

export const BADGES: Record<
  BadgeKey,
  { Icon: TierComponent; label: string }
> = {
  ...Object.fromEntries(
    ROLE_BADGES.map((r) => [r.key, { Icon: r.Icon, label: r.name }]),
  ) as Record<RoleKey, { Icon: TierComponent; label: string }>,
  /* the owner's badge is Tyler's circle-T glyph, labeled by the role */
  owner: { Icon: ROLE_BADGE.tyler, label: "Owner" },
  /* milestone badges — founders show their lettered hexagons (the
     goaded/roadsign role keys), there's no generic founder tile */
  joinday: { Icon: CakeTiers.Basic, label: "First Day" },
  first100: { Icon: ConfettiTiers.Golden, label: "First 100 Members" },
  /* the bug ladder — hunter / bounty / super of each */
  bug: { Icon: BugTiers.Basic, label: "Bug Hunter" },
  bugbounty: { Icon: BugTiers.Golden, label: "Bug Bounty" },
  goldbug: { Icon: BugTiers.Shiny, label: "Super Bug Hunter" },
  superbugbounty: { Icon: BugTiers.Impeccable, label: "Super Bug Bounty" },
};
